import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ResourceType } from 'gen/api';
import {
  DRAWER_MAX_HEIGHT,
  DRAWER_MAX_WIDTH,
  DRAWER_MIN_HEIGHT,
  DRAWER_MIN_WIDTH,
  PATENT_DRAWER_TABS,
} from '../../constants/constants';

export type DrawerAlignment = 'top' | 'bottom' | 'left' | 'right';

interface DrawerState {
  drawerOpen: boolean;
  workflowType: ResourceType | string;
  patentId: string;
  resultId: string;
  analysisRunId: string;
  workflowId: string;
  sessionId: string;
  searchQuery: string;
  oldSearchId: string;
  score: number | null;
  defaultTab: PATENT_DRAWER_TABS;
  isSimilarPatentsOpen: boolean;
  disableHighlighting?: boolean;
  drawerAlignment: DrawerAlignment;
  drawerWidth: number;
  drawerHeight: number;
}

const initialState: DrawerState = {
  drawerOpen: false,
  workflowType: '',
  resultId: '',
  patentId: '',
  analysisRunId: '',
  workflowId: '',
  sessionId: '',
  searchQuery: '',
  oldSearchId: '',
  score: null,
  defaultTab: PATENT_DRAWER_TABS.ASK_AI,
  isSimilarPatentsOpen: false,
  disableHighlighting: false,
  drawerAlignment: 'right',
  drawerWidth: 600,
  drawerHeight: 400,
};

const drawerPatentSlice = createSlice({
  name: 'drawer',
  initialState,
  reducers: {
    setDrawerOpen(state, action: PayloadAction<Partial<DrawerState> & { open: boolean }>) {
      state.drawerOpen = action.payload.open;

      Object.assign(state, action.payload);
    },
    setSimilarPatentsOpen(state, action: PayloadAction<{ isSimilarPatentsOpen: boolean }>) {
      state.isSimilarPatentsOpen = action.payload.isSimilarPatentsOpen;
    },
    setDrawerAlignment(state, action: PayloadAction<{ alignment: DrawerAlignment }>) {
      state.drawerAlignment = action.payload.alignment;
    },
    setDrawerWidth(state, action: PayloadAction<{ width: number }>) {
      state.drawerWidth = Math.max(DRAWER_MIN_WIDTH, Math.min(action.payload.width, DRAWER_MAX_WIDTH));
    },
    setDrawerHeight(state, action: PayloadAction<{ height: number }>) {
      state.drawerHeight = Math.max(DRAWER_MIN_HEIGHT, Math.min(action.payload.height, DRAWER_MAX_HEIGHT));
    },
    resetDrawerSlice: () => initialState,
  },
});

export const {
  setDrawerOpen,
  setSimilarPatentsOpen,
  setDrawerAlignment,
  setDrawerWidth,
  setDrawerHeight,
  resetDrawerSlice,
} = drawerPatentSlice.actions;
export default drawerPatentSlice.reducer;
